import { useCallback, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { closeMiniApp } from '@telegram-apps/sdk-react';
import { redirectToPromoterView } from '@helpers/redirectToPromoterView.helper';
import PromoterLayout from '@pages/main/components/Promoter.layout';
import { useScannerPage } from '@pages/scanner/hooks/useScannerPage.hook';
import GeneralError from '@components/GeneralError.component';
import { useIsPrinterStartParam } from '@hooks/features/useIsPrinterStartParam.hook';
import { useQrScanner } from '@hooks/features/useQrScanner.hook';

function Scanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    user,
    fetchQrSummation,
    setQrError,
    errorText,
    isReadyToScan,
    showPreloader,
    redirectTo,
  } = useScannerPage();

  const { isPrinterLink, printerId } = useIsPrinterStartParam();
  const { isInternal } = location.state || {};

  const {
    handleQrScanner,
    redirectToPromoterPage,
    redirectToPrinter,
    clearRedirectToPrinter,
    redirectPath,
  } = useQrScanner({
    redirectTo,
    fetchFn: fetchQrSummation,
    setError: setQrError,
  });

  const handleScanner = useCallback(
    () => handleQrScanner(t('main.scanner'), user),
    [handleQrScanner, t, user],
  );

  const isCanScan = isReadyToScan && !(isPrinterLink && !isInternal);

  useEffect(() => {
    if (isCanScan) {
      handleScanner();
    }
  }, [handleScanner, isCanScan]);

  useEffect(() => {
    if (redirectToPrinter) {
      const printerId = redirectToPrinter;
      clearRedirectToPrinter();
      navigate(`/printer/${printerId}`);
    }
  }, [navigate, redirectToPrinter, clearRedirectToPrinter]);

  useEffect(() => {
    if (redirectToPromoterPage && !errorText) {
      redirectToPromoterView(user.initiator_id, user.contact_id, user.chat_id);
    }
  }, [redirectToPromoterPage, user, errorText]);

  useLayoutEffect(() => {
    if (isPrinterLink && !isInternal) {
      navigate(`/printer/${printerId}`, {
        replace: true,
        state: { isInternal: true },
      });
    }
  }, [isPrinterLink, isInternal, navigate, printerId]);

  useEffect(() => {
    if (redirectPath && !errorText && !redirectToPromoterPage) {
      navigate(redirectPath, { replace: true });
    }
  }, [errorText, navigate, redirectPath, redirectToPromoterPage]);

  if (errorText) {
    return (
      <GeneralError
        showPreloader={showPreloader}
        errorText={errorText}
        onButtonClick={() => navigate(0)}
        onSecondaryButtonClick={() => closeMiniApp()}
      />
    );
  }

  if (user?.isPromoter) {
    return <PromoterLayout />;
  }
}

export default Scanner;
